<script setup>
import { ref, inject, nextTick } from 'vue'
import FilterSearch from "@/components/explorer/filter/FilterSearch.vue"
import BannerMsg from "@/components/common/BannerInfo.vue"

//events raised by component
const emit = defineEmits([
	'closeFilterItemPanel',
	'updateFilterValue'
])
const { userInputState } = inject('userInputState')

//componenet methods exposed to parent
defineExpose({
    initFilterItem
});

//binding used to invoke method on child component
const childRefFilterSearch = ref({})

//reactive field proxies
const  dimValues = ref([])
const nestedCols = ref()
const childName = ref()
const parentName = ref()
const openParents = ref(new Set())
const chartType = ref()
const controlType = ref()
const selectedRBValue = ref()
const showAccordion = ref(true)
const bannerMsg = ref('')
const itemRefs = ref({})
//const isDisabled = ref(false)

//local variables
let dim = {};

//methods
function initFilterItem(charttype, d, controltype, selectedArea) {
	openParents.value = new Set()
	nestedCols.value = d.nestedColumns
	childName.value = d.columnName
	parentName.value = d.parentColumnName
	dim = d
	chartType.value = charttype
	controlType.value = controltype

	showAccordion.value = true
	childRefFilterSearch.value.initSearch()
	if(controltype == 'rb') {
		selectedRBValue.value = dim.appliedValues.find(x => x.selected)?.name
	}

	if(selectedArea) {
		scrollView(selectedArea)
		nextTick()
		setTimeout(() => {
			toggleParent(selectedArea)
		}, .5)
	}

	if(charttype == "line")
		bannerMsg.value = 'Variables not chosen in Categories are filtered to one option.'
	else if(charttype == "bar" || charttype == "column")
		bannerMsg.value = 'Variables not chosen in X-Axis Categories and Group are filtered to one option.'
	else if(charttype == "tree")
		bannerMsg.value = 'Variables not chosen in Category and Detail are filtered to one option.'
	else if(charttype == "sankey")
		bannerMsg.value = 'Variables not chosen in Left and Right are filtered to one option.'
	else
		bannerMsg.value = ''

}

async function scrollView(selectedArea) {
	//selectedKey.value = selectedArea
	//isPanelOpen.value = true

	// Wait for the panel and its contents to be rendered
	await nextTick()
	const element = itemRefs.value[selectedArea]

	if (element) {
		element.scrollIntoView({
			behavior: 'smooth',
			block: 'center'
		})
	}
}

function setItemRef(key, el) {
	if (el) {
    	itemRefs.value[key] = el
  	}
}

function closeFilter(type) {
	emit('closeFilterItemPanel', type);
}

function updateFilter(filterValue) {
	//if this is a geography column:
	if(dim.geoLabelFlag) {
		userInputState.value.geo = true;
	}
	if(import.meta.env.VITE_APP_DEBUG) {
		console.log('updateFilter')
		console.log('userInputState.value.geo',userInputState.value.geo)
	}

	//special case: 'All' is only available as standalone filter for treemap and sankey:
 	if(chartType.value == 'treemap' || chartType.value == 'sankey') {
		const allVal = dim.totalLabel
		if(dim.distinctValues.includes(allVal)) {
			if(filterValue.name == allVal) { //user clicking "All" (either selecting or deselecting)
				dim.appliedValues.forEach(i => {
					if(i.name == allVal) {
						i.selected = filterValue.selected
					} else {
						i.selected = filterValue.selected?false:true;
					}
				})
			} else { //user clicking something other than "All" (either selecting or deselecting)
				dim.appliedValues.forEach(i => {
					if(filterValue.selected && i.name == allVal) {
						i.selected = false
					}
				})
			}
		}
	} else {
		//if control type = radio button then deselect all other items
		if(controlType.value == 'rb') {
			dim.appliedValues.forEach(i => {
				if(i.name == filterValue.name)
					i.selected = true
				else
					i.selected = false
			})
		}

		//sync parent selection state with children
		//Find parent group(s) for value selected
		const parents = dim.nestedColumns.filter(x => {
			if(x.appliedValues != null) {
				let t = x.appliedValues.find(y => y.name == filterValue.name && y.parentValue == filterValue.parentValue)
				if(t != null)
					return x
			}
		})
		//If any child in parent grp is selected then select parent
		if(parents.length > 0) {
			parents.forEach(parent => {
				let selectedFlag;
				if (areAnyChildrenSelected(parent)) {
					selectedFlag = true
				} else {
					selectedFlag = false
				}
				parent.selected = selectedFlag
			})
			//Additionally, if value belongs to a shared grp, sync up the shared groups entry with the individual groups - the shared grp is required for API
			//if any shared grp values selected then select all parents that have the shared values, for instance:
			// France is in both EU-27 and Europe, so it is assigned to Region (parent) of "EU-27; Europe"	
			let sharedValues = dim.nestedColumns.filter(x => x.sharedGrpFullValue)
			if(sharedValues.length > 0) {
				let selectedSharedVals = sharedValues.filter(y => y.selected)
				sharedValues.forEach(y => {
					const val = dim.nestedColumns.find(z => z.name == y.sharedGrpFullValue)
					val.selected = selectedSharedVals.findIndex(t => t.sharedGrpFullValue == val.name) > -1?true:false;
				})
			}
		}
	}
	emit('updateFilterValue', dim);
}

const shouldDisable = (checkboxId) => {
	//if(nestedCols.value.filter(x => x.selected).length < 2)
	const selected = nestedCols.value.filter(x => x.selected)
	return selected.length === 1 && selected[0].name === checkboxId
};

const getSubTitle = (grp)=>{
	const selected = grp.appliedValues.filter(x => {
		return x.selected // && x.visible
	})
	if(selected.length == 1)
		return selected[0].name
	else
		return `${selected.length} selected`
}

function resetAll() {
	//if this is a geography column:
	if(dim.geoLabelFlag) {
		userInputState.value.geo = false;
	}
	if(import.meta.env.VITE_APP_DEBUG) {
		console.log('updateFilter')
		console.log('userInputState.value.geo',userInputState.value.geo)
	}
	if(controlType.value == 'cb') {
		let allVal = dim.totalLabel
		let totalLabelFound = dim.distinctValues.includes(allVal)
		dim.selected = true;
		dim.appliedValues.forEach(i => {
			if(totalLabelFound && i.name == allVal) {
				i.selected = false;
			} else {
				i.selected = true;
			}
		})
	} else if(controlType.value == 'rb') {
		let defaultVal = dim.defaultValues.find(x => dim.distinctValues.includes(x))
		if(defaultVal !== undefined) {
			dim.appliedValues.forEach(i => {
				if(i.name == defaultVal) {		
					selectedRBValue.value = i.name
					i.selected = true;
				} else {
					i.selected = false;
				}
			})
		} else {
			let allVal = dim.totalLabel
			let totalLabelFound = dim.distinctValues.includes(allVal)
			dim.appliedValues.forEach(i => {
				if(totalLabelFound && i.name == allVal) {
					selectedRBValue.value = i.name	
					i.selected = true;
				} else {
					i.selected = false;
				}
			})
		}
	}
	emit('updateFilterValue', dim);
}

function isParentOpen(name) {
  return openParents.value.has(name)
}

function toggleParent(name) {
  if (openParents.value.has(name)) {
    openParents.value.delete(name)
  } else {
    openParents.value.add(name)
  }

  // Force reactivity update for Set
  openParents.value = new Set(openParents.value)
}

function areAllValuesSelected() {
	if(dim.appliedValues == null)
		return false
  	return dim.appliedValues.every(value =>
    	value.selected == true
  	)
}

function toggleAllValues() {
	if (areAllValuesSelected()) {
		dim.selected = false
		dim.appliedValues.forEach(value =>
			value.selected = false
		)
		dim.nestedColumns.forEach(value =>
			value.selected = false
		)
	} else {
		dim.selected = true
		dim.appliedValues.forEach(value =>
			value.selected = true
		)
		dim.nestedColumns.forEach(value =>
			value.selected = true
		)
	}
	emit('updateFilterValue', dim);
	return
}

function areAnyChildrenSelected(parent) {
  return parent.appliedValues.find(value =>
    value.selected == true
  )
}

function areAllChildrenSelected(parent) {
  return parent.appliedValues.every(value =>
    value.selected == true
  )
}

function toggleAllChildren(parent) {
	//if this is a geography column:
	if(dim.geoLabelFlag) {
		userInputState.value.geo = true;
	}
	if(import.meta.env.VITE_APP_DEBUG) {
		console.log('updateFilter')
		console.log('userInputState.value.geo',userInputState.value.geo)
	}
	let selectedFlag;
	if (areAllChildrenSelected(parent)) {
		selectedFlag = false
	} else {
		selectedFlag = true
	}
	//parent.selected = selectedFlag
	parent.selected = true;
	parent.appliedValues.forEach(value =>
		value.selected = selectedFlag
	)

	//sync up shared groups with displayed group - required for API
	//if any shared grp values selected then select all parents that have the shared values, for instance:
	// France is in both EU-27 and Europe, so it is assigned to Region (parent) of "EU-27; Europe"	
	let sharedValues = dim.nestedColumns.filter(x => x.sharedGrpFullValue)
 	if(sharedValues.length > 0) {
		let selectedSharedVals = sharedValues.filter(y => y.selected)
		sharedValues.forEach(y => {
			const val = dim.nestedColumns.find(z => z.name == y.sharedGrpFullValue)
			val.selected = selectedSharedVals.findIndex(t => t.sharedGrpFullValue == val.name) > -1?true:false;
		})
	}


	emit('updateFilterValue', dim);
	return
}

function areAllParentsOpen() {
	if(dim.nestedColumns) {
 		return dim.nestedColumns.every(value => isParentOpen(value.name))
	} else {
		return false
	}
}

function toggleOpenAllParents() {
	if (areAllParentsOpen()) {
		openParents.value = new Set()
	} else {
		dim.nestedColumns.forEach(value =>
			openParents.value.add(value.name)
		)
		// Force reactivity update for Set
		openParents.value = new Set(openParents.value)
	}
}

function filteredValues(matchedValues) {
	if(matchedValues !== null) {
		showAccordion.value = false
		// Create a new array with unique values using Set and the spread operator
		dimValues.value = [...new Set(matchedValues)];
	} else {
		dimValues.value = []
		showAccordion.value = true
	}
}
</script>

<template>
	<div class="accordion-panel">
		<div class="accordion-header">
			<button class="btn-icon btn-arrow" @click="closeFilter" aria-label="Back"><i class="fa-solid fa-chevron-left"></i> Back</button>
			<button class="btn-icon btn-close" @click="closeFilter('all')" aria-label="Close"><i class="fa-solid fa-times"></i></button>
		</div>
		<div class="accordion-title">{{childName}}</div>
		<div class="accordion-body">
			<FilterSearch ref="childRefFilterSearch" :dim="dim" @filteredValues="filteredValues" />
			<div id="search-results-section" v-show="!showAccordion">
				<div class="filters">
					<ul>
						<li v-for="dimVal in dimValues" :key="dimVal.name">
						<input type="checkbox" v-model="dimVal.selected" :disabled="shouldDisable(dimVal.name)"  @change="updateFilter(dimVal)">
						<div class="fld-value">{{ dimVal.name }}</div>
						</li>
					</ul>
				</div>
			</div>
			<div v-if="controlType == 'rb' && chartType != 'map' && chartType != 'table'">
				<BannerMsg :show=true :type="'info side'" style="margin-top:1.25rem" :msgContent="bannerMsg"></BannerMsg>
			</div>		
			<div id="accordion-section" v-show="showAccordion">
				<div class="accordion-expand-all">
					<button class="btn-select-all"
						@click="toggleOpenAllParents()">
					<span v-if="areAllParentsOpen()">
						{{'Collapse all '}}<i class="fa-solid fa-circle-minus"></i>
					</span>
					<span v-else>
						{{'Expand all '}}<i class="fa-solid fa-circle-plus"></i>
					</span>
					</button>
				</div>
				<div class="accordion-list">

					<div v-for="parent in nestedCols"
						:key="parent.name" :ref="el => setItemRef(parent.name, el)">
						<div v-if="!parent.name.includes(';')">
							<!-- Accordion header -->
							<button
								class="accordion-parent-item"
								@click="toggleParent(parent.name)">
								<div>
									<div>{{ parent.name }}</div>
									<div class="accordion-text">
										{{getSubTitle(parent)}}
									</div>
								</div>
								<div class="btn-expand-collapse">
									<span v-if="isParentOpen(parent.name)">
										<i class="fa-solid fa-circle-minus"></i>
									</span>
									<span v-else>
										<i class="fa-solid fa-circle-plus"></i>
									</span>
								</div>
							</button>
							<!-- Accordion content -->
							<div v-if="isParentOpen(parent.name)">
								<div v-if="controlType == 'cb'">
									<div class="select-all">
										<button class="btn-select-all"
											@click="toggleAllChildren(parent)">
										<span v-if="areAllChildrenSelected(parent)">
											{{'Unselect all in this ' + parentName}}
										</span>
										<span v-else>
											{{'Select all in this ' + parentName}}
										</span>
										</button>

									</div>								
									<div  v-for="child in parent.appliedValues"
										:key="child">
										<label class="accordion-child-item">
										<input
											type="checkbox"
											:value="child.name"
											:disabled="shouldDisable(child.name)"  @change="updateFilter(child)"
											v-model="child.selected" />
											<span class="accordion-label">{{ child.name }}</span>
										</label>
									</div>
								</div>
								<div v-if="controlType == 'rb'">
									<div  v-for="child in parent.appliedValues"
										:key="child">
										<div class="accordion-child-item">
											<label class="blue-radio">
												<input type="radio" 										
												:value="child.name"
												:disabled="shouldDisable(child.name)"  @change="updateFilter(child)"
												v-model="selectedRBValue" />
												<span class="blue-mark"></span>
												<span class="accordion-label">{{ child.name }}</span>
											</label>
																			
										</div>

									</div>
								</div>

							</div>
							<hr />
						</div>
					</div>
				</div>
			</div>
			<div v-if="controlType == 'cb'" class="button-section-container">
				<div class="button-section">
					<button class="btn-secondary"
							@click="toggleAllValues()">
						<span v-if="areAllValuesSelected()">
							<i class="fa-solid fa-check"></i> Unselect all
						</span>
						<span v-else>
							<i class="fa-solid fa-check"></i> Select all
						</span>
					</button>
					<button class="btn-primary" @click="resetAll"> <i class="fa-solid fa-trash-can-undo"></i> Reset all </button>
				</div>
			</div>
			<div v-if="controlType == 'rb'" class="button-section-container">
				<div class="button-section radio-btn">
					<button class="btn-primary" @click="resetAll"> <i class="fa-solid fa-trash-can-undo"></i> Reset all </button>
				</div>
			</div>		
		</div>
	</div>
</template>

<style scoped>
.accordion-panel {
	background-color: var(--surface-color-Default);
	display: flex;
	width: 25rem; /* 400px; */
	flex-direction: column;
	align-items: flex-start;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-xl, 1.5rem);
	flex: 1 1 0;
	padding: .75rem 1.25rem;
	border-radius: var(--ui-cards-default, 0.75rem);
	background: var(--surface-default, #FFF);
	height: 100%;
	/* UI Card */
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);

	position:absolute;
	top:0;
	left:0;

}
.accordion-header {
	display: flex;
	width: 100%; /* 394px; */
	height: 2.5rem;
	justify-content: space-between;
}
.accordion-body {
	display: flex;
	flex-direction: column;
	flex: 1 1 0;
	height: 100%;
	width: 100%;
	min-height: 0;
}
#accordion-section {
	display: flex;
  	flex-direction: column;
  	height: 100%;
	flex: 1 1 0;
}
.accordion-list {
	display: flex;
	padding-inline-start: 0;
	flex-direction: column;
	align-items: flex-start;
	list-style-type: none;
	overflow-y: auto;
	overflow-x: hidden;
	/* max-height: 480px; */
	/*	height: 27rem;*/
	flex: 1 1 0;
	/*height: 100%;*/
	min-height: 0;
}

.accordion-parent-item {
	display: flex;
	height: 4.1875rem;
	align-self: stretch;
	align-items: center;
	justify-content: space-between;
	text-align: left;
	border: 0;
	width:22rem;
	border-radius: 0;
	background-color: var(--surface-color-Default);
}

.accordion-child-item {
	height: 3rem;
	text-align: left;
	border: 0;
	width:100%;
	background-color: var(--surface-color-Default);
	display: flex;
	align-items: center;
}

.accordion-parent-item:hover, .accordion-child-item:hover {
	text-decoration: none;
	cursor: pointer;
}

.accordion-title {
	color: var(--text-Header, #171717);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H6, 1.25rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-H6, 1.75rem); /* 140% */
	text-align: left;
}

.accordion-label {
	color: var(--text-Primary, #2C2C2C);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	padding-left: .5rem;
}

.accordion-text {
	overflow: hidden;
	color: var(--text-Secondary, #757575);
	text-overflow: ellipsis;
	text-align: left;

	/* Regular/Caption */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Caption, 0.875rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Caption, 1.25rem); /* 142.857% */
	text-transform: uppercase;
}

.accordion-expand-all {
	display: flex;
	justify-content: end;
	width: 23rem;
}

.btn-close {
	color: var(--icon-cta-Blue, #064291);
	text-align: right;
	font-weight: 900;
	line-height: normal;
	font-size: var(--font-size-H6);
	border: 0;
	background-color: transparent;
	min-width: 1rem;
}
.btn-arrow {
	font-size: var(--font-size-Base);
	color: var(--icon-cta-Blue, #064291);
}
.btn-expand-collapse {
	min-width:0;
	color: var(--icon-cta-Blue, #064291);
	font-size: var(--font-size-Base, 16px);
	text-decoration: none;
}

.btn-select-all {
	text-align: right;
	border: 0;
	background-color: transparent;
	min-width: 1rem;

	color: var(--text-link-Active, #064291);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 16px);
	font-weight: 500;
	line-height: var(--font-line-height-Base, 24px); /* 150% */
}
.button-section-container {
	display: flex;
	flex-direction: column;
	min-height: 0;
}
.button-sectionOLD {
	display: flex;
	justify-content:space-between;
	width: 100%;
	border-top: 1px solid var(--border-color-Divider);
	/*margin-top: 1.25rem;*/
	margin-top: auto;
	padding-top: 1.25rem;
    /*position: absolute;*/
    bottom: 20px;
    left: 20px;
    width: stretch;
    right: 20px;	
}
.button-section {
	display: flex;
	justify-content:space-between;
	width: 100%;
	border-top: 1px solid var(--border-color-Divider);
	padding-top: 1rem;
	margin-top: auto;
}

.button-section.radio-btn{
	justify-content:flex-end;
}

button {
	text-decoration: none;
}

hr {
	color: var(--border-color-Divider, #A9B7C7);
	width: 100%;
	height: 1px;
	margin: 0;
}

input[type='checkbox'] {
	height:1.5rem;
	width:1.5rem;
}

/* search results section */
#search-results-section {
	display: flex;

}
.filters {
	display: flex;
	padding-inline-start: 0;
	flex-direction: column;
	align-items: flex-start;
	align-self: stretch;

	list-style-type: none;
	min-height: 32rem; /* 512px */
	overflow-y: auto;
	overflow-x: hidden;
}

ul {
	list-style-type: none;
    padding-left: 0;
    width: 100%;
}

li {
	display: flex;
	align-items: center;
	align-self: stretch;
	height: 3rem;
	width: 100%;
}

.fld-value {
	padding-left: .25rem;
	color: var(--text-primary, #2C2C2C);
	width: 100%;
	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
}

input[type='checkbox']:hover {
	cursor: pointer;
}


.blue-radio {
	display: flex;
	align-items: center;
	cursor: pointer;
	margin: 10px 0;
	position: relative;
	padding-left: 35px;
	font-size: 18px;
}

.blue-radio input[type="radio"] {
  display: none;
}

.blue-mark {
	position: absolute;
	left: 0;
	top: 0;
	height: 24px;
	width: 24px;
	background-color: #fff;
	border: 1px solid var(--icon-cta-Blue, #064291);;
	border-radius: 50%;
}

.blue-radio input[type="radio"]:checked + .blue-mark::after {
	content: "";
	position: absolute;
	top: 2px;
	left: 2px;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: var(--icon-cta-Blue, #064291);;
}

/* 1023px - iPad */
@media (max-width: 63.9375rem) {
	.accordion-panel {
		position:absolute;
		left: 0;
		top: 0;
		width: 100%;
		min-height: unset;
	}
}
</style>
